import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { usePortalName } from '@/hooks/usePortalName';
import { MEMBER_PORTAL_NAMES } from '@/data/constants';
import { UserCategoryTypeBC } from '@/data/models/user-category-type.model';
import { useUserCategoryTypeBC } from '@/hooks/useUserCategoryTypeBC';
import { useIsClientPortalUser } from '@/hooks/useIsClientPortalUser';
import { useIsClientPortalAdminUser } from '@/hooks/useIsClientPortalAdminUser';

type PortalContextValue = {
  isPortal: boolean;
  isMemberPortal: boolean;
  isClientPortal: boolean;
  isClientPortalAdminUser: boolean;
  portalName: string | null;
};

const PortalContext = createContext<PortalContextValue | null>(null);

function PortalProvider({ children }: { children: ReactNode }) {
  // Getting the portal name and whether it is a portal from the URL
  const portalName = usePortalName();
  const isPortal = portalName ? MEMBER_PORTAL_NAMES.includes(portalName) : false;

  // To determine if we're in a client portal, we need to check
  // the logged in user's permissions
  const isClientPortalUser = useIsClientPortalUser();
  const isClientPortalAdminUser = useIsClientPortalAdminUser();
  const isClientPortal = isClientPortalUser || isClientPortalAdminUser;

  // To determine if we're in a member portal, we need to check
  // the logged in user's category type
  const userCategoryType = useUserCategoryTypeBC();
  const isMemberPortal = userCategoryType === UserCategoryTypeBC.Passenger;

  return (
    <PortalContext.Provider value={{ isPortal, isMemberPortal, isClientPortal, isClientPortalAdminUser, portalName }}>
      {children}
    </PortalContext.Provider>
  );
}

function usePortalContext(): PortalContextValue {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortalContext must be used within PortalProvider');
  }
  return {
    isPortal: context.isPortal,
    isMemberPortal: context.isMemberPortal,
    isClientPortal: context.isClientPortal,
    isClientPortalAdminUser: context.isClientPortalAdminUser,
    portalName: context.portalName,
  };
}

export { PortalProvider, usePortalContext };
